import React from 'react';
import Button from '../Common/Button';
import { ContentText } from '../Common/ContentText';
import { EquipmentItem } from '../../types/character';

// Generic item type that can represent both equipment and spells
interface GenericItem {
  id: string;
  name: string;
  description?: string;
  category: string;
  type: string;
  tags?: string[];
  modifiers?: any[];
  // Equipment specific (optional)
  equipped?: boolean;
  // Spell specific (optional)
  domain?: string;
  level?: number;
  requirements?: string[];
  // Raw data for type-specific properties
  [key: string]: any;
}

interface ItemDetailsProps {
  item: GenericItem | EquipmentItem;
  characterId?: string;
  showAddButton?: boolean;
  onAddToCharacter?: () => void;
  onRemoveFromCharacter?: () => void;
  className?: string;
  buttonState?: 'default' | 'success' | 'removed';
  alreadyAdded?: boolean;
}

const ItemDetails: React.FC<ItemDetailsProps> = ({
  item,
  characterId,
  showAddButton = false,
  onAddToCharacter,
  onRemoveFromCharacter,
  className = '',
  buttonState = 'default',
  alreadyAdded = false,
}) => {
  // Helper function for title case formatting
  const toTitleCase = (str: string): string => {
    return str.replace(/\w\S*/g, (txt) => {
      return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase();
    });
  };

  return (
    <div className={`equipment-details ${className}`}>
      <h3>{item.name}</h3>
      <div className="equipment-details__info">
        <div className="equipment-details__basic">
          <p><strong>Name:</strong> {item.name}</p>
          <p><strong>Type:</strong> {toTitleCase(item.type)}</p>
          
          {/* Weapon-specific details */}
          {item.category === 'weapon' && (
            <>
              {(item as any).subtype && <p><strong>Subtype:</strong> {toTitleCase((item as any).subtype)}</p>}
              <p><strong>Category:</strong> {toTitleCase((item as any).originalCategory || item.category)}</p>
              {(item as any).tier && <p><strong>Tier:</strong> {(item as any).tier}</p>}
              {(item as any).trait && <p><strong>Trait:</strong> {toTitleCase((item as any).trait)}</p>}
              {(item as any).range && <p><strong>Range:</strong> {toTitleCase((item as any).range)}</p>}
              {(item as any).damage && <p><strong>Damage:</strong> {(item as any).damage}</p>}
              {(item as any).damageType && <p><strong>Damage Type:</strong> {toTitleCase((item as any).damageType)}</p>}
              {(item as any).burden && <p><strong>Burden:</strong> {toTitleCase((item as any).burden)}</p>}
            </>
          )}
          
          {/* Armor-specific details */}
          {item.category === 'armor' && (
            <>
              {(item as any).tier && <p><strong>Tier:</strong> {(item as any).tier}</p>}
              {(item as any).baseThresholds?.major && <p><strong>Major Threshold:</strong> {(item as any).baseThresholds.major}</p>}
              {(item as any).baseThresholds?.severe && <p><strong>Severe Threshold:</strong> {(item as any).baseThresholds.severe}</p>}
              {(item as any).baseArmorScore && <p><strong>Base Armor Score:</strong> {(item as any).baseArmorScore}</p>}
            </>
          )}
          
          {/* Wheelchair-specific details */}
          {item.category === 'wheelchair' && (
            <>
              {(item as any).subtype && <p><strong>Subtype:</strong> {toTitleCase((item as any).subtype)}</p>}
              {(item as any).tier && <p><strong>Tier:</strong> {(item as any).tier}</p>}
              {(item as any).frame && <p><strong>Frame:</strong> {toTitleCase((item as any).frame)}</p>}
              {(item as any).trait && <p><strong>Trait:</strong> {toTitleCase((item as any).trait)}</p>}
              {(item as any).range && <p><strong>Range:</strong> {toTitleCase((item as any).range)}</p>}
              {(item as any).damage && <p><strong>Damage:</strong> {(item as any).damage}</p>}
              {(item as any).damageType && <p><strong>Damage Type:</strong> {toTitleCase((item as any).damageType)}</p>}
              {(item as any).burden && <p><strong>Burden:</strong> {toTitleCase((item as any).burden)}</p>}
            </>
          )}

          {/* Spell/Domain Card-specific details */}
          {(item.category === 'spell' || item.type === 'domain-card') && (
            <>
              {(item as any).domain && <p><strong>Domain:</strong> {toTitleCase((item as any).domain)}</p>}
              {(item as any).level && <p><strong>Level:</strong> {(item as any).level}</p>}
              <p><strong>Type:</strong> {toTitleCase(item.type)}</p>
            </>
          )}
        </div>
        
        {/* Feature section */}
        {(item as any).feature && (
          <div className="equipment-details__feature">
            <h4>Feature</h4>
            <ContentText content={(item as any).feature} />
          </div>
        )}
        
        {/* Description section */}
        {item.description && (
          <div className="equipment-details__description">
            <h4>Description</h4>
            <ContentText content={item.description} />
          </div>
        )}
        
        {/* Effects/Modifiers section */}
        {item.modifiers && item.modifiers.length > 0 && (
          <div className="equipment-details__modifiers">
            <h4>Effects</h4>
            {item.modifiers.map((modifier, index) => (
              <div key={index} className="modifier-item">
                <strong>Effect:</strong> {`${modifier.operation} ${modifier.value} to ${modifier.target}`}
              </div>
            ))}
          </div>
        )}
        
        {/* Requirements section - for spells */}
        {(item as any).requirements && (item as any).requirements.length > 0 && (
          <div className="equipment-details__requirements">
            <h4>Requirements</h4>
            <ul>
              {(item as any).requirements.map((req: any, index: number) => (
                <li key={index}>{typeof req === 'string' ? req : JSON.stringify(req)}</li>
              ))}
            </ul>
          </div>
        )}
        
        {/* Character action button for desktop view */}
        {showAddButton && characterId && (onAddToCharacter || onRemoveFromCharacter) && (
          <div className="equipment-details__actions">
            {alreadyAdded && onRemoveFromCharacter ? (
              <Button
                variant="SECONDARY"
                size="MEDIUM"
                onClick={onRemoveFromCharacter}
                disabled={buttonState === 'removed'}
                className={`equipment-details__add-button ${buttonState === 'removed' ? 'equipment-details__add-button--removed' : ''}`}
              >
                {buttonState === 'removed' ? '✓ Removed from Character!' : '✗ Remove from Character'}
              </Button>
            ) : onAddToCharacter ? (
              <Button
                variant="PRIMARY"
                size="MEDIUM"
                onClick={onAddToCharacter}
                disabled={buttonState === 'success'}
                className={`equipment-details__add-button ${buttonState === 'success' ? 'equipment-details__add-button--success' : ''}`}
              >
                {buttonState === 'success' ? '✓ Added to Character!' : 'Add to Character'}
              </Button>
            ) : null}
          </div>
        )}
      </div>
    </div>
  );
};

export default ItemDetails;